import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { LogOut, ChevronLeft, ChevronRight, Sun, Moon, Menu, X } from "lucide-react";
import clsx from "clsx";

import { useAuth } from "../guardarToken/authContext";
import { useTheme } from "../guardarToken/themeContext";
import { NAV_ADMIN, NAV_USER, NAV_SUPERADMIN } from "./navConfig";
import AvatarPicker from '../elementos/AvatarPicker.jsx';

const navItemsSuperAdmin = NAV_SUPERADMIN;
const navItemsAdmin = NAV_ADMIN;
const navItemsUser = NAV_USER;

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const authContext = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [avatarAnchor, setAvatarAnchor] = useState(null);
  const [avatar, setAvatar] = useState(() => localStorage.getItem('avatar') || 'emoji:🧑');

  // Obtener tipoUsuario del contexto PRIMERO, localStorage como fallback
  const rawTipoUsuario = authContext?.tipoUsuario || localStorage.getItem('tipoUsuario') || '';
  const tipoUsuario = typeof rawTipoUsuario === 'string' ? rawTipoUsuario.toLowerCase().trim() : '';
  const storedUserName = authContext?.nombreUsuario || localStorage.getItem('nombreUsuario') || '';

  const navItems = tipoUsuario === "superadmin" ? navItemsSuperAdmin : (tipoUsuario === "admin" ? navItemsAdmin : navItemsUser);
  const titulo = tipoUsuario === "superadmin" ? "Super Admin" : (tipoUsuario === "admin" ? "Admin" : "Usuario");

  const handleAvatarOpen = (e) => setAvatarAnchor(e.currentTarget);
  const handleAvatarClose = () => setAvatarAnchor(null);
  const handleAvatarSelect = (value) => {
    setAvatar(value);
    localStorage.setItem('avatar', value);
  };

  // Debug: Log cuando cambia el tipo de usuario
  useEffect(() => {
    console.log('Sidebar - tipo de usuario:', tipoUsuario);
    console.log('Sidebar - contexto:', authContext);
  }, [tipoUsuario, authContext]);

  const handleCerrar = () => {
    if (authContext?.cerrarSesion) {
      authContext.cerrarSesion();
    } else {
      localStorage.clear();
    }
    navigate('/');
    window.location.reload();
  };

  // Si el contexto está validando, mostrar loading (después de todos los hooks)
  if (authContext?.isValidating) {
    return (
      <aside className="fixed left-0 top-0 h-screen w-56 border-r flex items-center justify-center bg-gray-900 border-slate-700">
        <div className="text-white text-sm">Cargando...</div>
      </aside>
    );
  }

  return (
    <>
      {/* Botón hamburguesa para móvil */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className={clsx(
          "fixed top-4 left-4 z-[60] lg:hidden p-2 rounded-lg transition-all duration-200",
          isDarkMode
            ? "bg-slate-800 text-white hover:bg-slate-700"
            : "bg-white text-gray-800 hover:bg-gray-100 shadow-md"
        )}
      >
        {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Overlay para móvil */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={clsx(
        "fixed left-0 top-0 h-screen border-r flex flex-col shadow-lg transition-all duration-300 overflow-hidden",
        isDarkMode
          ? "bg-slate-900 border-slate-700"
          : "bg-white border-gray-200",
        // Desktop: siempre visible
        "lg:z-50",
        isCollapsed ? "lg:w-16" : "lg:w-56",
        // Móvil: oculto por defecto, aparece con botón
        "z-50",
        isMobileOpen ? "translate-x-0 w-64" : "-translate-x-full lg:translate-x-0",
        isCollapsed && !isMobileOpen ? "lg:w-16" : ""
      )}>
        <div className={clsx(
          "flex flex-col items-center justify-center py-4 border-b select-none relative flex-shrink-0",
          isDarkMode
            ? "border-slate-700 bg-gradient-to-r from-slate-800 to-slate-900"
            : "border-gray-200 bg-gradient-to-r from-orange-50 to-orange-100"
        )}>
          {!isCollapsed && (
            <>
              <span className={clsx(
                "text-xl font-extrabold tracking-wide",
                isDarkMode ? "text-orange-500" : "text-orange-600"
              )}>COMMANDA</span>
              <span className={clsx(
                "text-sm font-medium text-center mt-1",
                isDarkMode ? "text-gray-300" : "text-gray-700"
              )}>{titulo}</span>
              {/* Avatar y nombre */}
              <div className="flex flex-col items-center gap-2 mt-3">
                <button
                  onClick={handleAvatarOpen}
                  className={clsx(
                    "w-12 h-12 rounded-full grid place-items-center transition-all duration-200 hover:scale-110 cursor-pointer shadow-md",
                    isDarkMode ? "bg-slate-700 hover:bg-slate-600" : "bg-orange-200 hover:bg-orange-300"
                  )}
                  title="Cambiar avatar"
                >
                  {avatar.startsWith('emoji:') ? (
                    <span className="text-2xl leading-none" aria-hidden="true">{avatar.split(':')[1]}</span>
                  ) : (
                    <img src={avatar} alt="avatar" className="w-12 h-12 rounded-full object-cover" />
                  )}
                </button>
                <span className={clsx(
                  "text-xs font-medium text-center px-2 truncate max-w-full",
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                )}>{storedUserName || 'Usuario'}</span>
              </div>
            </>
          )}
          {isCollapsed && (
            <button
              onClick={handleAvatarOpen}
              className={clsx(
                "w-10 h-10 rounded-full grid place-items-center transition-all duration-200 hover:scale-110 cursor-pointer shadow-md",
                isDarkMode ? "bg-slate-700 hover:bg-slate-600" : "bg-orange-200 hover:bg-orange-300"
              )}
              title={storedUserName ? `${storedUserName} - Cambiar avatar` : "Cambiar avatar"}
            >
              {avatar.startsWith('emoji:') ? (
                <span className="text-xl leading-none" aria-hidden="true">{avatar.split(':')[1]}</span>
              ) : (
                <img src={avatar} alt="avatar" className="w-10 h-10 rounded-full object-cover" />
              )}
            </button>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={clsx(
              "absolute -right-3 top-1/2 -translate-y-1/2 rounded-full p-1 hover:bg-orange-600 hover:border-orange-500 transition-colors shadow-md hidden lg:block",
              isDarkMode
                ? "bg-slate-800 border border-slate-600"
                : "bg-white border border-gray-300"
            )}
          >
            {isCollapsed ? <ChevronRight className="w-4 h-4 text-orange-400" /> : <ChevronLeft className="w-4 h-4 text-orange-400" />}
          </button>
        </div>
        <AvatarPicker anchorEl={avatarAnchor} open={Boolean(avatarAnchor)} onClose={handleAvatarClose} onSelect={handleAvatarSelect} />
        <nav className="flex-1 py-2 px-2 overflow-y-auto min-h-0 custom-scrollbar">
          {navItems.map((group, groupIdx) => (
            <div key={group.group || groupIdx} className={clsx(groupIdx > 0 && "mt-4")}>
              {group.group && !isCollapsed && (
                <div className={clsx(
                  "px-3 mb-1 text-[10px] font-black uppercase tracking-[0.2em]",
                  isDarkMode ? "text-slate-500" : "text-slate-400"
                )}>
                  {group.group}
                </div>
              )}
              {group.group && isCollapsed && (
                <div className={clsx(
                  "border-t my-2 mx-2",
                  isDarkMode ? "border-slate-800" : "border-gray-100"
                )} />
              )}
              <ul className="space-y-1">
                {group.items.map(({ label, icon: Icon, to, color }) => (
                  <li key={to}>
                    <Link
                      to={to}
                      title={isCollapsed ? label : ""}
                      onClick={() => setIsMobileOpen(false)}
                      className={clsx(
                        "group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 relative",
                        location.pathname.startsWith(to)
                          ? "bg-orange-600 text-white shadow-md shadow-orange-950/20"
                          : isDarkMode
                            ? "text-gray-400 hover:bg-slate-800 hover:text-white"
                            : "text-gray-700 hover:bg-orange-50 hover:text-orange-600",
                        isCollapsed && "lg:justify-center"
                      )}
                    >
                      {/* Acento lateral cuando activo */}
                      <span
                        className={clsx(
                          "absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-r transition-all duration-300",
                          location.pathname.startsWith(to) ? "bg-orange-400 opacity-100 scale-y-100" : "bg-transparent opacity-0 scale-y-0"
                        )}
                      />
                      <Icon className={clsx(
                        "w-5 h-5 flex-shrink-0 transition-colors duration-200",
                        location.pathname.startsWith(to) ? "text-white" : (isDarkMode ? (color || "text-gray-400") : "text-gray-500 group-hover:text-orange-600")
                      )} />
                      <span className={clsx(
                        "truncate",
                        isCollapsed && "lg:hidden",
                      )}>{label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className={clsx(
          "px-2 py-3 border-t space-y-2 flex-shrink-0 bg-opacity-100",
          isDarkMode ? "border-slate-700 bg-slate-900" : "border-gray-200 bg-white"
        )}>
          <button
            onClick={toggleTheme}
            title={isCollapsed ? (isDarkMode ? "Modo claro" : "Modo oscuro") : ""}
            className={clsx(
              "flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200",
              isDarkMode
                ? "text-gray-300 hover:bg-slate-800 hover:text-orange-400"
                : "text-gray-700 hover:bg-gray-100 hover:text-orange-600",
              isCollapsed && "lg:justify-center"
            )}
          >
            {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            <span className={clsx(
              isCollapsed && "lg:hidden"
            )}>{isDarkMode ? "Modo claro" : "Modo oscuro"}</span>
          </button>
          <button
            onClick={handleCerrar}
            title={isCollapsed ? "Cerrar sesión" : ""}
            className={clsx(
              "flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200",
              isDarkMode
                ? "text-gray-300 hover:bg-red-900 hover:text-red-300"
                : "text-gray-700 hover:bg-red-50 hover:text-red-600",
              isCollapsed && "lg:justify-center"
            )}
          >
            <LogOut className="w-5 h-5" />
            <span className={clsx(
              isCollapsed && "lg:hidden"
            )}>Cerrar sesión</span>
          </button>
        </div>
      </aside>
    </>
  );
}
